/**
 * Asset-game eval scoring.
 *
 * Static (~ms, no LLM) checks that a generated project HONOURS its planned
 * asset manifest the way the snake scorer checks framework idioms. Each check
 * is an independent regex/predicate over the project files producing a
 * pass/fail + label — same shape and report format as scoring/snake.ts.
 *
 * This is the STATIC sibling of the in-loop asset-contract guard
 * (assets/assetContract). It never runs
 * the game; it grades the source for: a present non-empty manifest, complete
 * composite pairs, the this.$assets.client read + draw idioms, zero path
 * drift from the manifest, animation frame-name integrity, no hallucinated
 * $assets.client methods, no retired AssetManager.* usage, and at least one
 * test that probes assets.
 *
 * Input is a flat ProjectFile[] (path + content). Output reuses snake.ts's
 * ScoreResult/ScoreCheck so formatScoreReport works on it unchanged.
 */

import { extractFile, ASSET_MANAGER_METHODS } from '../knowledge/extractor';
import type { ProjectFile, AssetSpecPayload } from '../types';
import type { ScoreCheck, ScoreResult } from './snake';

const stripSlash = (p: string): string => p.replace(/^\/+/, '');

/** `$assets.client` methods that actually exist on the framework surface.
 *  Anything else captured by `.$assets.client.<method>` is a hallucination.
 *  Sourced from the extractor so the path-method list and this set can never
 *  disagree. */
const KNOWN_ASSET_METHODS = new Set<string>(ASSET_MANAGER_METHODS);

/** Bare `AssetManager.<method>` — the retired pre-migration idiom. Any
 *  occurrence at all (known method name or not) is drift: the manifest
 *  prompt exclusively teaches `this.$assets.client.*` now. */
const RE_RETIRED_ASSET_MANAGER = /\bAssetManager\.\w+/;

/** `<slug>:<number>` frame-name literal, e.g. 'walk:0'. */
const RE_FRAME_LITERAL = /['"`]([A-Za-z_][\w-]*:\d+)['"`]/g;

const isSpecFile = (path: string): boolean =>
  /\.spec\.|\.test\./.test(path) || /(^|\/)__tests__\//.test(path);

export function scoreAssets(files: ProjectFile[], specs: AssetSpecPayload[]): ScoreResult {
  const clientFiles = files.filter((f) => stripSlash(f.path).startsWith('client/'));
  const clientCode = clientFiles.map((f) => f.content).join('\n');
  const allCode = files.map((f) => f.content).join('\n');

  // Union of every declared manifest part path (image + json + audio).
  const manifestPaths = new Set(specs.flatMap((s) => s.parts.map((p) => stripSlash(p.path))));

  // Every asset path the CODE/TESTS reference via AssetManager string literals
  // (reuse the knowledge extractor — single source of the path regex).
  const referencedPaths = [...new Set(files.flatMap((f) => extractFile(f.content).assetUses.map(stripSlash)))];

  const animationSpecs = specs.filter((s) => s.composite === 'animation');
  const allFrameNames = new Set(animationSpecs.flatMap((s) => s.frames ?? []));

  // ── Check 7: animation frame-name integrity ──────────────────────────────
  // Every `<slug>:<number>` literal in code must be a declared frame name.
  // SKIP (auto-pass) when there are no animation specs — nothing to drift from.
  let frameNamesOk = true;
  if (animationSpecs.length > 0) {
    for (const f of files) {
      for (const m of f.content.matchAll(RE_FRAME_LITERAL)) {
        if (!allFrameNames.has(m[1])) {
          frameNamesOk = false;
          break;
        }
      }
      if (!frameNamesOk) break;
    }
  }

  // ── Check 8: no hallucinated $assets.client methods ──────────────────────
  let assetMethodsOk = true;
  for (const m of allCode.matchAll(/\.\$assets\.client\.(\w+)/g)) {
    if (!KNOWN_ASSET_METHODS.has(m[1])) {
      assetMethodsOk = false;
      break;
    }
  }

  // ── Check 9: no retired AssetManager.* usage ─────────────────────────────
  const retiredAssetManagerOk = !RE_RETIRED_ASSET_MANAGER.test(allCode);

  const checks: ScoreCheck[] = [
    // ── Manifest ─────────────────────────────────────────────────────────
    {
      category: 'Manifest',
      label: 'Manifest non-empty',
      pass: specs.length > 0,
    },
    {
      category: 'Manifest',
      label: 'Composite pairs complete (animation/tileset have image + json)',
      pass: specs
        .filter((s) => s.composite === 'animation' || s.composite === 'tileset')
        .every(
          (s) =>
            s.parts.some((p) => p.kind === 'image') && s.parts.some((p) => p.kind === 'json'),
        ),
    },

    // ── Code idioms ──────────────────────────────────────────────────────
    {
      category: 'Code',
      label: 'Client calls this.$assets.client.getImage( with a string literal',
      pass: /\.\$assets\.client\.getImage\(\s*['"`][^'"`]+['"`]/.test(clientCode),
    },
    {
      category: 'Code',
      label: 'Client calls ctx.drawImage(',
      pass: /\bctx\.drawImage\s*\(/.test(clientCode),
    },

    // ── Manifest agreement ───────────────────────────────────────────────
    {
      category: 'Manifest',
      label: 'No asset-path drift (every $assets.client path is in the manifest)',
      pass: referencedPaths.every((p) => manifestPaths.has(p)),
    },
    {
      category: 'Manifest',
      label: 'Animation frame names match the manifest',
      pass: frameNamesOk,
    },

    // ── Hallucinations ───────────────────────────────────────────────────
    {
      category: 'Hallucinations',
      label: 'No hallucinated $assets.client methods',
      pass: assetMethodsOk,
    },
    {
      category: 'Hallucinations',
      label: 'No retired AssetManager usage',
      pass: retiredAssetManagerOk,
    },

    // ── Tests ────────────────────────────────────────────────────────────
    {
      category: 'Tests',
      label: 'A generated test asserts on assets (expectSpriteDrawn / expectAssetLoaded)',
      pass: files.some(
        (f) => isSpecFile(stripSlash(f.path)) && /expectSpriteDrawn|expectAssetLoaded/.test(f.content),
      ),
    },
  ];

  const passed = checks.filter((c) => c.pass).length;
  const total = checks.length;

  const byCategory: Record<string, { passed: number; total: number }> = {};
  for (const c of checks) {
    const slot = (byCategory[c.category] ||= { passed: 0, total: 0 });
    slot.total++;
    if (c.pass) slot.passed++;
  }

  return {
    checks,
    passed,
    total,
    pct: Math.round((passed / total) * 100),
    byCategory,
  };
}
